import { useEffect, useMemo, useState } from 'react';
import { Camera, ChevronRight, Keyboard, Mic, Pencil, Trash2 } from 'lucide-react';
import type { Formulation, Measurement, MeasurementKey, Source } from '../lib/types';
import { MEASUREMENT_FIELDS, MEASUREMENT_KEYS } from '../lib/types';
import { formatDate, formatValue } from '../lib/format';
import { deleteFormulation } from '../services/formulations';
import { warningsFor } from '../lib/validation';
import { Button, Input, Modal, Select, cx } from './ui';
import { useIsDesktop } from '../hooks/useIsDesktop';
import type { ConfirmationSource } from './ConfirmationScreen';

const SOURCE_ICON: Record<Source, typeof Camera> = {
  photo: Camera,
  voice: Mic,
  manual: Keyboard,
};

/** A blank cell means "not measured". It must never look like a zero. */
function MeasurementCell({
  m,
  fieldKey,
  readOnly,
  onClick,
}: {
  m: Measurement | null;
  fieldKey: MeasurementKey;
  readOnly: boolean;
  onClick: () => void;
}) {
  const spec = MEASUREMENT_FIELDS[fieldKey];
  const Icon = m ? SOURCE_ICON[m.source] : null;
  const warned = m
    ? warningsFor(fieldKey, { value: m.value, sd: m.sd }).some((w) => w.severity === 'warn')
    : false;

  return (
    <button
      onClick={onClick}
      disabled={readOnly}
      title={m ? `${spec.label} — entered by ${m.source}` : readOnly ? spec.label : `Add ${spec.label}`}
      className={cx(
        'group/cell flex w-full items-baseline justify-end gap-1.5 rounded-md px-2 py-1 text-right tabular-nums transition-colors',
        'pointer-coarse:min-h-11',
        !readOnly && 'hover:bg-ink-800',
        readOnly && 'cursor-default',
        warned && 'text-amber-300',
      )}
    >
      {m ? (
        <>
          <span className="font-medium">{formatValue(m.value, fieldKey)}</span>
          {m.sd !== null && (
            <span className="text-xs text-ink-400">± {formatValue(m.sd, fieldKey)}</span>
          )}
          {Icon && <Icon size={11} className="shrink-0 text-ink-400" />}
        </>
      ) : (
        <span className="text-ink-600 group-hover/cell:text-ink-400">—</span>
      )}
    </button>
  );
}

type SortKey = 'date-desc' | 'date-asc' | 'name-asc' | 'name-desc';

const SORTS: { value: SortKey; label: string }[] = [
  { value: 'date-desc', label: 'Newest first' },
  { value: 'date-asc', label: 'Oldest first' },
  { value: 'name-asc', label: 'Name A-Z' },
  { value: 'name-desc', label: 'Name Z-A' },
];

/** Milliseconds, or null when a formulation has no date. */
function dateMs(f: Formulation): number | null {
  const t = f.measuredAt as unknown as { toMillis?: () => number } | null;
  if (!t) return null;
  return typeof t.toMillis === 'function' ? t.toMillis() : new Date(t as never).getTime();
}

/** Local yyyy-mm-dd, to compare against what a date input produces. */
function isoDay(ms: number): string {
  const d = new Date(ms);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export default function FormulationTable({
  projectId,
  formulations,
  readOnly,
  canManage,
  onEdit,
  onCapture,
}: {
  projectId: string;
  formulations: Formulation[];
  readOnly: boolean;
  /**
   * Owner-only. An editor adds readings but does not rewrite what a
   * formulation is, or remove one — changing a label silently reassigns every
   * reading under it, and a delete takes them all with it. Kept separate from
   * `readOnly` because an editor is emphatically not read-only.
   */
  canManage: boolean;
  onEdit: (f: Formulation) => void;
  onCapture: (source: ConfirmationSource) => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState<Formulation | null>(null);
  /**
   * Collapsed by default, and only one row open at a time.
   *
   * A project accumulates dozens of formulations, and a full row each - four
   * measurements, their SDs, N/P, modification, notes - turns finding one into
   * scrolling. The list answers "which formulation, measured when"; opening a
   * row answers everything else. Restricting it to one keeps the page short
   * enough that the pager still means something.
   */
  const [openId, setOpenId] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>('date-desc');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);

  const isDesktop = useIsDesktop();
  // Ten fills a phone screen without scrolling past the pager; a desktop row is
  // the same height but the viewport is far taller, so it takes more.
  const perPage = isDesktop ? 25 : 10;

  const visible = useMemo(() => {
    const filtered = formulations.filter((f) => {
      if (!from && !to) return true;
      const ms = dateMs(f);
      // An undated formulation cannot satisfy a date range; excluding it is the
      // honest answer, and it is still reachable by clearing the filter.
      if (ms === null) return false;
      const day = isoDay(ms);
      if (from && day < from) return false;
      if (to && day > to) return false;
      return true;
    });

    const byName = (a: Formulation, b: Formulation) =>
      (a.label || '').localeCompare(b.label || '', undefined, {
        numeric: true,
        sensitivity: 'base',
      });

    return [...filtered].sort((a, b) => {
      switch (sort) {
        case 'name-asc':
          return byName(a, b);
        case 'name-desc':
          return byName(b, a);
        default: {
          // Undated rows sort last either way - they are unplaceable in time,
          // not infinitely old.
          const am = dateMs(a);
          const bm = dateMs(b);
          if (am === null && bm === null) return byName(a, b);
          if (am === null) return 1;
          if (bm === null) return -1;
          return sort === 'date-asc' ? am - bm : bm - am;
        }
      }
    });
  }, [formulations, from, to, sort]);

  const pageCount = Math.max(1, Math.ceil(visible.length / perPage));
  // A filter that shortens the list must not strand the reader on a page that
  // no longer exists.
  const current = Math.min(page, pageCount - 1);
  const rows = visible.slice(current * perPage, current * perPage + perPage);

  useEffect(() => setPage(0), [from, to, sort, perPage]);

  /** Any measurement cell opens the same confirmation screen, in manual mode. */
  const editField = (f: Formulation, key: MeasurementKey): ConfirmationSource => ({
    mode: 'manual',
    captureId: null,
    instrument: MEASUREMENT_FIELDS[key].instrument,
    extraction: null,
    transcript: null,
    previewUrl: null,
    storagePath: null,
    targetFormulationId: f.id,
    focusKey: key,
  });

  const filtering = !!from || !!to;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="min-w-0">
          <label className="field-label mb-1 block" htmlFor="f-sort">
            Sort
          </label>
          <Select id="f-sort" value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
            {SORTS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="field-label mb-1 block" htmlFor="f-from">
            Measured from
          </label>
          <Input id="f-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="field-label mb-1 block" htmlFor="f-to">
            to
          </label>
          <Input id="f-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        {filtering && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFrom('');
              setTo('');
            }}
          >
            Clear dates
          </Button>
        )}
        <span className="ml-auto self-center text-xs text-ink-400">
          {visible.length} of {formulations.length}
        </span>
      </div>

      {visible.length === 0 ? (
        <p className="rounded-xl border border-ink-700 bg-ink-900 p-6 text-center text-sm text-ink-400">
          No sample was measured in that range.
        </p>
      ) : (
        <ul className="overflow-hidden rounded-xl border border-ink-700">
          {rows.map((f) => {
            const open = openId === f.id;
            return (
              <li key={f.id} className="border-b border-ink-800 last:border-0">
                <div className="flex items-center gap-2 bg-ink-900/40">
                  <button
                    onClick={() => setOpenId(open ? null : f.id)}
                    aria-expanded={open}
                    className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2.5 text-left hover:bg-ink-800/60 pointer-coarse:min-h-12"
                  >
                    <ChevronRight
                      size={14}
                      className={cx(
                        'shrink-0 text-ink-500 transition-transform',
                        open && 'rotate-90',
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">
                      {f.label || <span className="text-ink-600">Untitled</span>}
                    </span>
                    <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-ink-400">
                      {formatDate(f.measuredAt) || '-'}
                    </span>
                  </button>
                  <div className={cx('flex shrink-0 gap-1 pr-2', !canManage && 'hidden')}>
                    <button
                      onClick={() => onEdit(f)}
                      className="icon-btn text-ink-400 hover:bg-ink-800"
                      aria-label={`Edit ${f.label}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => setConfirmDelete(f)}
                      className="icon-btn text-ink-400 hover:bg-red-950 hover:text-red-300"
                      aria-label={`Delete ${f.label}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {open && (
                  <div className="border-t border-ink-800 bg-ink-900/70 px-3 py-3">
                    {(f.np !== null || f.modification) && (
                      <p className="mb-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-400">
                        {f.np !== null && <span>N/P {f.np}</span>}
                        {f.modification && <span className="text-ink-300">{f.modification}</span>}
                      </p>
                    )}
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {MEASUREMENT_KEYS.map((key) => (
                        <div key={key} className="rounded-lg bg-ink-800/60 px-2 py-1.5">
                          <span className="field-label">{MEASUREMENT_FIELDS[key].shortLabel}</span>
                          <MeasurementCell
                            m={f[key]}
                            fieldKey={key}
                            readOnly={readOnly}
                            onClick={() => onCapture(editField(f, key))}
                          />
                        </div>
                      ))}
                    </div>
                    {f.notes && <p className="mt-2 text-xs text-ink-400">{f.notes}</p>}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {pageCount > 1 && (
        <div className="mt-3 flex items-center justify-between gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={current === 0}
            onClick={() => setPage(current - 1)}
          >
            Previous
          </Button>
          <span className="text-xs tabular-nums text-ink-400">
            Page {current + 1} of {pageCount}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={current >= pageCount - 1}
            onClick={() => setPage(current + 1)}
          >
            Next
          </Button>
        </div>
      )}

      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Delete sample">
        <p className="text-sm text-ink-300">
          Delete{' '}
          <strong className="text-ink-100">{confirmDelete?.label || 'this sample'}</strong> and
          its measurements? Captures that fed it are kept for the audit trail.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              if (confirmDelete) await deleteFormulation(projectId, confirmDelete.id);
              setConfirmDelete(null);
            }}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
}
